import { cx } from "@emotion/css";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import MarkChatReadOutlinedIcon from "@mui/icons-material/MarkChatReadOutlined";
import { LoadingButton } from "@mui/lab";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { LocalAnswers } from "src/components/Exam/Answers/AnswersTypes";
import { LocalOptions } from "src/components/NewComponents/Exam/Option/LocalOptions";
import { useSlot } from "src/components/Slots/SlotsContext";
import { NotificationStatus } from "src/helpers/notificationsTypes";
import { isLdek, isLek } from "src/helpers/services";
import { useMark } from "src/hooks/useMark";
import { useRemoveNotification } from "src/hooks/useNotifications";
import { refetchQuestions } from "src/hooks/useQuestions";
import { isStaff } from "src/hooks/useUser";
import { useQuestionsListSettings } from "src/reducers/questions";
import { ExamStatus, ExamTypes } from "src/types/enums";

import isPes from "../../../../helpers/isPes";
import Question from "../../../Exam/QuestionBox/Question";

const ResolveNotification = ({ notification }) => {
	const [isLoading, setIsLoading] = useState(false);
	const { mutateAsync } = useRemoveNotification();
	const { t } = useTranslation();
	const getPathToQuestion = () => {
		if (isPes()) {
			return `https://egzaminlek.pl/admin/el_app/pytaniepesstaff/${notification.itemId}/change/`;
		}

		if (isLek()) {
			return `https://egzaminlek.pl/admin/el_app/lepapppytaniecomparedbstaff/${notification.itemId}/change/`;
		}

		if (isLdek()) {
			return `https://egzaminldek.pl/admin/eld_app/pytaniecomparedbstaff/${notification.itemId}/change/`;
		}

		return null;
	};

	return (
		<div className="p-absolute top-4 right-4">
			<LoadingButton
				component={Link}
				to={{
					pathname: getPathToQuestion(),
				}}
				target="_blank"
				rel="noreferrer noopener"
				startIcon={<EditNoteOutlinedIcon fontSize="small" />}
			>
				{t("notifications.editQuestionsButton")}
			</LoadingButton>
			<LoadingButton
				loading={isLoading}
				onClick={async () => {
					setIsLoading(true);
					await mutateAsync(notification);
					await refetchQuestions();
					setIsLoading(false);
				}}
				color="primary"
				startIcon={<MarkChatReadOutlinedIcon fontSize="small" />}
			>
				{t("notifications.resolveNotification")}
			</LoadingButton>
		</div>
	);
};

function QuestionView({
	question,
	setAnswer,
	searchValue,
	disabledSettings,
	className,
	startInEdit = false,
}) {
	const { state } = useQuestionsListSettings();
	const searchTerms = (searchValue || "")
		.split(" ")
		.map((term) => term.trim())
		.filter((term) => term.length > 1);
	const ref = useMark(searchTerms, {
		className: "search-highlight-mark",
	});
	const EditorSlot = useSlot("questionEditor");

	const renderResolveNotification = () => {
		if (
			!question.notification ||
			question.notification.status !== NotificationStatus.waiting ||
			!isStaff()
		) {
			return null;
		}

		return <ResolveNotification notification={question.notification} />;
	};

	const content = (
		<div ref={ref} className={cx("display-contents")}>
			<Question
				questionDetails={question.details}
				state={state}
				question={question}
				isAnswerSet={false}
			/>
			<LocalAnswers
				question={question}
				setAnswer={setAnswer}
				viewOnly={false}
				examStatus={ExamStatus.ACTIVE}
				examType={ExamTypes.BASIC}
			/>
		</div>
	);

	return (
		<div className={cx("question-view", className)} key={question.id}>
			{renderResolveNotification()}
			{EditorSlot ? (
				<EditorSlot
					key={question.id}
					question={question}
					startInEdit={startInEdit}
				>
					{content}
				</EditorSlot>
			) : (
				content
			)}
			{!disabledSettings && <LocalOptions question={question} />}
		</div>
	);
}
export default QuestionView;
